/**
 * Bundle 100 (Phase 0) - shared survey field renderers.
 *
 * Extracted verbatim from the pre-Bundle-100 SurveyTokenPage.jsx to
 * keep behaviour identical during the refactor. Renderers stay
 * schema-driven (drive off the question record from the API).
 *
 * No em/en dashes, no eslint-disable, no photo/image in alt text.
 */
import React from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Checkbox } from '../../ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../ui/select';

export const RatingStars = ({ value, onChange, testId,
                              lowLabel = 'Poor', highLabel = 'Excellent',
                              legend = null, ariaLabel = 'Rating' }) => (
  <div className="space-y-1">
    <div role="radiogroup" aria-label={ariaLabel} className="flex gap-2" data-testid={testId}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          type="button"
          role="radio"
          aria-checked={value === n}
          aria-label={legend && legend[n - 1] ? `${n} - ${legend[n - 1]}` : `${n} out of 5`}
          key={n}
          onClick={() => onChange(n)}
          className={`h-10 w-10 rounded-full border text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-primary-500 ${
            value === n
              ? 'bg-primary-900 text-white border-primary-900'
              : 'bg-white text-primary-800 border-neutral-200'
          }`}
          data-testid={`${testId}-${n}`}
        >
          {n}
        </button>
      ))}
    </div>
    {legend && legend.length === 5 ? (
      <div className="grid grid-cols-5 gap-2 text-[10px] uppercase tracking-widest text-neutral-500 mt-1" data-testid={`${testId}-legend`}>
        {legend.map((label, i) => (
          <span key={i} className="text-center">{label}</span>
        ))}
      </div>
    ) : (
      <div className="flex justify-between text-[10px] uppercase tracking-widest text-neutral-500 mt-1">
        <span>{lowLabel}</span>
        <span>{highLabel}</span>
      </div>
    )}
  </div>
);

export const RadioList = ({ q, value, onChange }) => (
  <div
    role="radiogroup"
    aria-labelledby={`q-${q.id}-legend`}
    className="space-y-2"
    data-testid={`q-${q.id}`}
  >
    {(q.options || []).map((o) => {
      const key = o.key || o;
      const label = o.label || o;
      const selected = value === key;
      return (
        <button
          key={key}
          type="button"
          role="radio"
          aria-checked={selected}
          onClick={() => onChange(key)}
          className={`w-full text-left px-3 py-2 rounded-md border text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${
            selected
              ? 'bg-primary-900 text-white border-primary-900'
              : 'bg-white text-primary-800 border-neutral-200'
          }`}
          data-testid={`q-${q.id}-${key}`}
        >
          {label}
        </button>
      );
    })}
  </div>
);

export const RatingWithNa = ({ q, value, onChange }) => {
  const isNa = value === 'na';
  const legend = q.legend || null;
  return (
    <div className="space-y-2" data-testid={`q-${q.id}`}>
      <div role="radiogroup" aria-label={q.label} className="flex gap-2">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            type="button" role="radio"
            aria-checked={value === n}
            aria-label={legend && legend[n - 1] ? `${n} - ${legend[n - 1]}` : `${n} out of 5`}
            key={n}
            onClick={() => onChange(n)}
            className={`h-10 w-10 rounded-full border text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-primary-500 ${
              value === n
                ? 'bg-primary-900 text-white border-primary-900'
                : 'bg-white text-primary-800 border-neutral-200'
            }`}
            data-testid={`q-${q.id}-${n}`}
          >
            {n}
          </button>
        ))}
        <button
          type="button" role="radio"
          aria-checked={isNa}
          onClick={() => onChange(isNa ? null : 'na')}
          className={`px-3 rounded-full border text-xs focus:outline-none focus:ring-2 focus:ring-primary-500 ${
            isNa ? 'bg-primary-900 text-white border-primary-900' : 'bg-white text-primary-800 border-neutral-200'
          }`}
          data-testid={`q-${q.id}-na`}
        >
          {q.na_label || 'I did not participate'}
        </button>
      </div>
      {legend && legend.length === 5 ? (
        <div className="grid grid-cols-5 gap-2 text-[10px] uppercase tracking-widest text-neutral-500 mt-1" data-testid={`q-${q.id}-legend`}>
          {legend.map((label, i) => (
            <span key={i} className="text-center">{label}</span>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export const ChipGroup = ({ value, onChange, options, maxSelect, testId,
                            liveRef }) => {
  const selected = Array.isArray(value) ? value : [];
  const toggle = (o) => {
    if (selected.includes(o)) {
      onChange(selected.filter((x) => x !== o));
    } else if (selected.length < (maxSelect || 99)) {
      onChange([...selected, o]);
    } else if (liveRef && liveRef.current) {
      // Bundle 100 (item H) - aria-live announcement on cap hit.
      liveRef.current.textContent = `Maximum ${maxSelect} selected`;
    }
  };
  return (
    <div className="space-y-1" data-testid={testId}>
      <div className="flex gap-2 flex-wrap">
        {(options || []).map((o) => {
          const active = selected.includes(o);
          return (
            <button
              key={o} type="button"
              role="button"
              aria-pressed={active}
              onClick={() => toggle(o)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                active
                  ? 'bg-primary-900 text-white border-primary-900'
                  : 'bg-white text-primary-800 border-neutral-200'
              }`}
              data-testid={`${testId}-${String(o).toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
            >
              {active ? (
                <span className="inline-flex items-center gap-1">
                  <span aria-hidden="true">{'\u2713'}</span>{o}
                </span>
              ) : o}
            </button>
          );
        })}
      </div>
      {maxSelect ? (
        <div className="text-[11px] text-neutral-500">
          {selected.length} of {maxSelect} selected
        </div>
      ) : null}
    </div>
  );
};

/**
 * Bundle 100 (item C) - Direct connection sub-card. Auto-generated
 * once per `direct_contact_count`. connection_type is REQUIRED.
 */
export const DirectConnectionCard = ({ index, row, capture_fields, onPatch,
                                       onRemove, errors }) => {
  const patch = (k, v) => onPatch({ ...(row || {}), [k]: v });
  const invalidKeys = new Set(
    (errors || []).map((e) => {
      const m = String(e).match(/direct_contacts\[(\d+)\]\.(\w+)/);
      return m && Number(m[1]) === index ? m[2] : null;
    }).filter(Boolean),
  );
  const cardId = `direct-connection-${index}`;
  return (
    <fieldset
      className="border border-neutral-200 rounded-md p-3 space-y-2"
      data-testid={`direct-connection-card-${index}`}
      aria-labelledby={`${cardId}-legend`}
    >
      <legend
        id={`${cardId}-legend`}
        className="text-xs font-semibold text-primary-900 px-1"
      >{`Direct connection ${index}`}</legend>
      {(capture_fields || []).map((f) => {
        const fid = `${cardId}-${f.key}`;
        const invalid = invalidKeys.has(f.key);
        const err_id = invalid ? `${fid}-err` : undefined;
        if (f.type === 'radio') {
          return (
            <div key={f.key} className="space-y-1">
              <div id={`${fid}-legend`} className="text-xs font-medium text-primary-800">
                {`Direct connection ${index} ${f.label.toLowerCase()}`}
                {f.required ? <span className="text-red-500 ml-1" aria-hidden="true">*</span> : null}
              </div>
              <div
                role="radiogroup"
                aria-labelledby={`${fid}-legend`}
                aria-invalid={invalid || undefined}
                aria-describedby={err_id}
                className="flex flex-wrap gap-2"
              >
                {(f.options || []).map((o) => {
                  const selected = row?.[f.key] === o.key;
                  return (
                    <button
                      key={o.key}
                      type="button" role="radio"
                      aria-checked={selected}
                      onClick={() => patch(f.key, o.key)}
                      className={`px-3 py-1 rounded-md border text-xs focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                        selected
                          ? 'bg-primary-900 text-white border-primary-900'
                          : 'bg-white text-primary-800 border-neutral-200'
                      }`}
                      data-testid={`${fid}-${o.key}`}
                    >{o.label}</button>
                  );
                })}
              </div>
              {invalid ? (
                <div id={err_id} className="text-xs text-red-700" data-testid={`${fid}-err`}>
                  Please choose a connection type.
                </div>
              ) : null}
            </div>
          );
        }
        return (
          <div key={f.key} className="space-y-1">
            <label htmlFor={fid} className="text-xs font-medium text-primary-800 block">
              {`Direct connection ${index} ${f.label.toLowerCase()}`}
            </label>
            <Input
              id={fid}
              value={row?.[f.key] || ''}
              onChange={(e) => patch(f.key, e.target.value)}
              aria-label={`Direct connection ${index} ${f.label.toLowerCase()}`}
              data-testid={fid}
            />
          </div>
        );
      })}
      <div className="flex justify-end">
        <Button
          type="button" variant="ghost" size="sm"
          onClick={onRemove}
          aria-label={`Remove direct connection ${index}`}
          className="text-neutral-500 hover:text-red-600 shrink-0"
          data-testid={`direct-connection-card-${index}-remove`}
        >Remove</Button>
      </div>
    </fieldset>
  );
};

/**
 * Bundle 100 (item D) - Missed connection request with full 7 fields
 * + required per-request confirmation checkbox.
 */
export const MissedConnectionRequest = ({ index, row, capture_fields,
                                         maxRequests, onPatch, onRemove,
                                         errors }) => {
  const patch = (k, v) => onPatch({ ...(row || {}), [k]: v });
  const invalidKeys = new Set(
    (errors || []).map((e) => {
      const m = String(e).match(/missed_connections\[(\d+)\]\.(\w+)/);
      return m && Number(m[1]) === index ? m[2] : null;
    }).filter(Boolean),
  );
  const cardId = `missed-connection-${index}`;
  return (
    <fieldset
      className="space-y-2 border border-neutral-200 rounded-md p-3"
      data-testid={`missed-connection-card-${index}`}
      aria-labelledby={`${cardId}-legend`}
    >
      <legend
        id={`${cardId}-legend`}
        className="text-xs font-semibold text-primary-900 px-1"
      >{`Request ${index} of ${maxRequests}`}</legend>
      {(capture_fields || []).map((f) => {
        const fid = `${cardId}-${f.key}`;
        const invalid = invalidKeys.has(f.key);
        const err_id = invalid ? `${fid}-err` : undefined;
        if (f.type === 'radio') {
          return (
            <div key={f.key} className="space-y-1">
              <div id={`${fid}-legend`} className="text-xs font-medium text-primary-800">
                {f.label}{f.required ? <span className="text-red-500 ml-1" aria-hidden="true">*</span> : null}
              </div>
              <div
                role="radiogroup"
                aria-labelledby={`${fid}-legend`}
                aria-invalid={invalid || undefined}
                aria-describedby={err_id}
                className="flex flex-wrap gap-2"
              >
                {(f.options || []).map((o) => {
                  const selected = row?.[f.key] === o.key;
                  return (
                    <button
                      key={o.key} type="button" role="radio"
                      aria-checked={selected}
                      onClick={() => patch(f.key, o.key)}
                      className={`px-3 py-1 rounded-md border text-xs focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                        selected
                          ? 'bg-primary-900 text-white border-primary-900'
                          : 'bg-white text-primary-800 border-neutral-200'
                      }`}
                      data-testid={`${fid}-${o.key}`}
                    >{o.label}</button>
                  );
                })}
              </div>
              {invalid ? (
                <div id={err_id} className="text-xs text-red-700" data-testid={`${fid}-err`}>
                  Please make a selection.
                </div>
              ) : null}
            </div>
          );
        }
        if (f.type === 'confirmation_checkbox') {
          return (
            <label
              key={f.key}
              htmlFor={fid}
              className="flex items-start gap-2 cursor-pointer"
              data-testid={fid}
            >
              <Checkbox
                id={fid}
                checked={!!row?.[f.key]}
                onCheckedChange={(v) => patch(f.key, !!v)}
                aria-invalid={invalid || undefined}
                aria-describedby={err_id}
              />
              <span className="text-xs text-primary-800 leading-relaxed">
                {f.label}{f.required ? <span className="text-red-500 ml-1" aria-hidden="true">*</span> : null}
              </span>
              {invalid ? (
                <span id={err_id} className="text-xs text-red-700 block" data-testid={`${fid}-err`}>
                  Please confirm before we contact this person.
                </span>
              ) : null}
            </label>
          );
        }
        return (
          <div key={f.key} className="space-y-1">
            <label htmlFor={fid} className="text-xs font-medium text-primary-800 block">
              {f.label}{f.required ? <span className="text-red-500 ml-1" aria-hidden="true">*</span> : null}
            </label>
            <Input
              id={fid}
              value={row?.[f.key] || ''}
              onChange={(e) => patch(f.key, e.target.value)}
              aria-invalid={invalid || undefined}
              aria-describedby={err_id}
              data-testid={fid}
            />
            {invalid ? (
              <div id={err_id} className="text-xs text-red-700" data-testid={`${fid}-err`}>
                {`${f.label} is required.`}
              </div>
            ) : null}
          </div>
        );
      })}
      <div className="flex justify-end">
        <Button
          type="button" variant="ghost" size="sm"
          onClick={onRemove}
          aria-label={`Remove missed connection request ${index}`}
          className="text-neutral-500 hover:text-red-600"
          data-testid={`${cardId}-remove`}
        >Remove</Button>
      </div>
    </fieldset>
  );
};

/**
 * Schema-driven field renderer. Dispatches on `q.type`.
 */
export const QuestionField = ({ q, value, onChange, answers,
                                onNestedRowPatch, listErrors, liveRef }) => {
  if (q.hidden) return null;
  if (q.type === 'rating_1_5') {
    return <RatingStars value={value} onChange={onChange} testId={`q-${q.id}`}
                          lowLabel={q.low_label} highLabel={q.high_label}
                          legend={q.legend} ariaLabel={q.label} />;
  }
  if (q.type === 'rating_1_5_with_na') {
    return <RatingWithNa q={q} value={value} onChange={onChange} />;
  }
  if (q.type === 'radio') return <RadioList q={q} value={value} onChange={onChange} />;
  if (q.type === 'chips') {
    return <ChipGroup value={value} onChange={onChange} options={q.options}
                        maxSelect={q.max_select} testId={`q-${q.id}`}
                        liveRef={liveRef} />;
  }
  if (q.type === 'longtext') {
    const val = value || '';
    const min = q.min_length || 0;
    const max = q.max_length || 500;
    return (
      <div className="space-y-1">
        <Textarea
          value={val}
          onChange={(e) => onChange(e.target.value.slice(0, max))}
          placeholder={min ? `At least ${min} characters` : ''}
          className="min-h-[120px]"
          data-testid={`q-${q.id}`}
          aria-describedby={`q-${q.id}-count`}
          maxLength={max}
        />
        <div id={`q-${q.id}-count`}
              className="text-[11px] text-neutral-500 flex justify-between"
              data-testid={`q-${q.id}-count`}>
          <span>{min ? (val.length >= min
                          ? `Minimum reached`
                          : `${min - val.length} more character(s) needed`) : ''}</span>
          <span>{val.length} / {max}</span>
        </div>
      </div>
    );
  }
  if (q.type === 'shorttext') {
    return <Input value={value || ''} onChange={(e) => onChange(e.target.value)} data-testid={`q-${q.id}`} />;
  }
  if (q.type === 'integer') {
    return (
      <Input
        type="number"
        inputMode="numeric"
        min={q.min ?? 0}
        max={q.max ?? 99}
        value={value ?? ''}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === '') { onChange(null); return; }
          const n = parseInt(raw, 10);
          if (Number.isNaN(n)) return;
          const clamped = Math.max(q.min ?? 0, Math.min(q.max ?? 99, n));
          onChange(clamped);
        }}
        className="w-32"
        aria-label={q.label}
        data-testid={`q-${q.id}`}
      />
    );
  }
  if (q.type === 'checkbox') {
    const cbId = `q-${q.id}-cb`;
    return (
      <label htmlFor={cbId} className="flex items-start gap-2 cursor-pointer"
              data-testid={`q-${q.id}`}>
        <Checkbox id={cbId} checked={!!value} onCheckedChange={(v) => onChange(!!v)} />
        <span className="text-sm text-primary-800 leading-relaxed">{q.label}</span>
      </label>
    );
  }
  if (q.type === 'select') {
    return (
      <Select value={String(value || '')} onValueChange={(v) => onChange(v)}>
        <SelectTrigger className="w-full" data-testid={`q-${q.id}`}
                        aria-label={q.label}>
          <SelectValue placeholder="Choose an option" />
        </SelectTrigger>
        <SelectContent>
          {(q.options || []).map((o) => (
            <SelectItem key={o.key || o} value={o.key || o}>{o.label || o}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }
  if (q.type === 'attendance_toggle') return null;
  if (q.type === 'direct_contact_list') {
    // Bundle 100 (item C) - auto-generate cards based on the driver.
    const count = Number((answers || {})[q.count_driver || 'direct_contact_count'] || 0);
    const list = Array.isArray(value) ? value : [];
    const rows = [];
    for (let i = 0; i < count; i += 1) rows.push(list[i] || {});
    const setRow = (i, next) => {
      const clone = [...rows];
      clone[i] = next;
      onChange(clone);
    };
    const removeRow = (i) => {
      const clone = rows.filter((_, idx) => idx !== i);
      onChange(clone);
    };
    if (count <= 0) {
      return (
        <div className="text-xs text-neutral-500" data-testid={`q-${q.id}-empty`}>
          Enter a count above to add connection cards.
        </div>
      );
    }
    return (
      <div className="space-y-2" data-testid={`q-${q.id}`}>
        {rows.map((row, i) => (
          <DirectConnectionCard
            key={i}
            index={i + 1}
            row={row}
            capture_fields={q.capture_fields}
            errors={listErrors}
            onPatch={(next) => setRow(i, next)}
            onRemove={() => removeRow(i)}
          />
        ))}
      </div>
    );
  }
  if (q.type === 'missed_connection_list') {
    const list = Array.isArray(value) ? value : [];
    const max = q.max_requests || 2;
    const setRow = (i, next) => {
      const clone = [...list];
      clone[i] = next;
      onChange(clone);
    };
    const removeRow = (i) => {
      const clone = list.filter((_, idx) => idx !== i);
      onChange(clone);
    };
    return (
      <div className="space-y-3" data-testid={`q-${q.id}`}>
        {q.explanation ? (
          <div className="text-xs text-primary-700 border border-neutral-200 rounded-md p-2"
                data-testid={`q-${q.id}-explanation`}>
            {q.explanation}
          </div>
        ) : null}
        {list.map((row, i) => (
          <MissedConnectionRequest
            key={i}
            index={i + 1}
            row={row}
            capture_fields={q.capture_fields}
            maxRequests={max}
            errors={listErrors}
            onPatch={(next) => setRow(i, next)}
            onRemove={() => removeRow(i)}
          />
        ))}
        {list.length < max ? (
          <Button
            variant="outline" size="sm" type="button"
            onClick={() => onChange([...list, {}])}
            aria-label="Add another missed connection request"
            data-testid={`q-${q.id}-add`}
          >Add another missed-connection request</Button>
        ) : (
          <div className="text-[11px] text-neutral-500" data-testid={`q-${q.id}-max`}>
            Maximum of {max} requests per event.
          </div>
        )}
      </div>
    );
  }
  return <div className="text-xs text-neutral-500">Unsupported: {q.type}</div>;
};

export default QuestionField;
